import { Button, Form, Input, InputNumber, Modal, Space, Spin, Typography } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ILoanDrawdownItem from '~Api/Loan/ILoanDrawdownItem';
import {
    loanDrawdownGetAction,
    loanDrawdownItemAddAction,
    loanDrawdownItemDeleteAction,
    loanDrawdownItemValueSetAction,
    loanDrawdownValueSetAction,
    loanGetAction,
} from '~Loans/actions';
import {
    loanDrawdownItemsSelector,
    loanDrawdownSelector,
    loanSelector,
} from '~Loans/selectors';
import { getLoanDrawdownTotalToBeDisbursed } from '~Loans/utilities';
import { IGlobalState } from '~reducer';
import { currencyFormatter } from '~utilities/formatters';
import { IDictionary } from '~utilities/IDictionary';

interface ILoanDrawdownItemErrors {
    amount?: string;
    description?: string;
}

interface ILoanDrawdownErrors {
    approvedDrawdownAmount?: string;
    disbursalTotal?: string;
    items: IDictionary<ILoanDrawdownItemErrors>;
    retainedContingencyAmount?: string;
    retainedFundsAmount?: string;
}

export default function DrawdownDisbursalCalculationModal(props: {
    loanUuid: string;
    loanDrawdownUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { isOpen, loanUuid, loanDrawdownUuid, onCancel } = props;

    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, loanUuid));
    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));
    const drawdownItems: IDictionary<ILoanDrawdownItem> = useSelector((state: IGlobalState) => loanDrawdownItemsSelector(state, loanDrawdownUuid));

    const [ errors, setErrors ] = useState<ILoanDrawdownErrors>({ items: {} });

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdown || !drawdownItems) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        drawdown,
        drawdownItems,
        loanDrawdownUuid,
    ]);

    useEffect(() => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    const totalToBeDisbursed: number = getLoanDrawdownTotalToBeDisbursed(drawdown, drawdownItems);

    const validateDisbursalTotal: () => boolean = useCallback(() => {
        let error: string = null;

        const disbursalTotal: number = (drawdown.retainedContingencyAmount ?? 0) + (drawdown.retainedFundsAmount ?? 0);
        if (_.round(disbursalTotal, 2) !== _.round(totalToBeDisbursed, 2)) {
            error = `Disbursal Total does not satisfy the Total To Be Disbursed of ${currencyFormatter.format(totalToBeDisbursed)}`;
        }

        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            disbursalTotal: error,
        }));

        return !error;
    }, [
        drawdown.retainedContingencyAmount,
        drawdown.retainedFundsAmount,
        setErrors,
        totalToBeDisbursed,
    ]);

    const onBlurValidateApprovedDrawdownAmount: () => boolean = useCallback(() => {
        let error: string = null;
        if (drawdown.approvedDrawdownAmount === null) {
            error = 'Please enter an amount';
        } else if (drawdown.approvedDrawdownAmount < 0) {
            error = 'Amount must be greater than $0';
        }
        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            approvedDrawdownAmount: error,
        }));

        error = validateDisbursalTotal() && error;

        return !error;
    }, [
        drawdown.approvedDrawdownAmount,
        setErrors,
        validateDisbursalTotal,
    ]);

    const validateLineItemDescription: (loanDrawdownItemUuid: string) => boolean = useCallback((loanDrawdownItemUuid: string) => {
        const loanDrawdownItem: ILoanDrawdownItem = drawdownItems[loanDrawdownItemUuid];
        let error: string = null;

        if (!loanDrawdownItem.description) {
            error = 'Please enter a description';
        }

        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            items: {
                ...errors.items,
                [loanDrawdownItemUuid]: {
                    ...(errors.items[loanDrawdownItemUuid] ?? {}),
                    description: error,
                },
            },
        }));

        return !error;
    }, [
        drawdownItems,
        setErrors,
    ]);

    const validateLineItemAmount: (loanDrawdownItemUuid: string) => boolean = useCallback((loanDrawdownItemUuid: string) => {
        let error: string = null;
        const loanDrawdownItem: ILoanDrawdownItem = drawdownItems[loanDrawdownItemUuid];
        if (loanDrawdownItem.amount === null) {
            error = 'Please enter an amount';
        }

        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            items: {
                ...errors.items,
                [loanDrawdownItemUuid]: {
                    ...(errors.items[loanDrawdownItemUuid] ?? {}),
                    amount: error,
                },
            },
        }));

        error = validateDisbursalTotal() && error;

        return !error;
    }, [
        drawdownItems,
        setErrors,
        validateDisbursalTotal,
    ]);

    const onBlurValidateRetainedContingencyAmount: () => boolean = useCallback(() => {
        let error: string = null;

        if (drawdown.retainedContingencyAmount > loan.retainedContingency) {
            error = 'The amount exceeds the available funds';
        }

        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            retainedContingencyAmount: error,
        }));

        error = validateDisbursalTotal() && error;

        return !error;
    }, [
        drawdown.retainedContingencyAmount,
        loan.retainedContingency,
        setErrors,
        validateDisbursalTotal,
    ]);

    const onBlurValidateRetainedFundsAmount: () => boolean = useCallback(() => {
        let error: string = null;

        if (drawdown.retainedFundsAmount > loan.retainedFunds) {
            error = 'The amount exceeds the available funds';
        }

        setErrors((errors: ILoanDrawdownErrors) => ({
            ...errors,
            retainedFundsAmount: error,
        }));

        error = validateDisbursalTotal() && error;

        return !error;
    }, [
        drawdown.retainedFundsAmount,
        loan.retainedFunds,
        setErrors,
        validateDisbursalTotal,
    ]);

    const onChangeApprovedDrawdownAmount: (approvedDrawdownAmount: number) => void = useCallback((approvedDrawdownAmount: number) => {
        dispatch(loanDrawdownValueSetAction(loanDrawdownUuid, 'approvedDrawdownAmount', approvedDrawdownAmount));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onChangeRetainedFundsAmount: (retainedFundsAmount: number) => void = useCallback((retainedFundsAmount: number) => {
        dispatch(loanDrawdownValueSetAction(loanDrawdownUuid, 'retainedFundsAmount', retainedFundsAmount));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onChangeRetainedContingencyAmount: (retainedContingencyAmount: number) => void = useCallback((retainedContingencyAmount: number) => {
        dispatch(loanDrawdownValueSetAction(loanDrawdownUuid, 'retainedContingencyAmount', retainedContingencyAmount));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onChangeItemAmount: (loanDrawdownItemUuid: string, amount: number) => void = useCallback((loanDrawdownItemUuid: string, amount: number) => {
        dispatch(loanDrawdownItemValueSetAction(loanDrawdownItemUuid, 'amount', amount));
    }, [
        dispatch,
    ]);

    const onChangeItemDescription: (loanDrawdownItemUuid: string, event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((loanDrawdownItemUuid: string, event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(loanDrawdownItemValueSetAction(loanDrawdownItemUuid, 'description', event.target.value));
    }, [
        dispatch,
    ]);

    const onClickAddLineItem: () => void = useCallback(() => {
        let sortOrder: number = 0;
        _.each(drawdownItems, (drawdownItem: ILoanDrawdownItem) => {
            if (sortOrder < drawdownItem.sortOrder) {
                sortOrder = drawdownItem.sortOrder;
            }
        });
        sortOrder += 1;

        const loanDrawdownItem: ILoanDrawdownItem = {
            amount: null,
            description: null,
            loanDrawdownUuid,
            sortOrder,
        };

        dispatch(loanDrawdownItemAddAction(loanDrawdownItem));
    }, [
        dispatch,
        drawdownItems,
        loanDrawdownUuid,
    ]);

    const onClickDeleteItem: (loanDrawdownItemUuid: string) => void = useCallback((loanDrawdownItemUuid: string) => {
        Modal.confirm({
            okText: 'Delete',
            onOk: () => {
                dispatch(loanDrawdownItemDeleteAction(loanDrawdownItemUuid));
            },
            title: 'Delete Line Item',
        });
    }, [
        dispatch,
    ]);

    const footer: ReactElement = (
        <Button className='close' onClick={onCancel}>Close</Button>
    );

    if (!drawdown || !drawdownItems || !loan) {
        return (
            <Modal
                footer={footer}
                destroyOnClose={true}
                open={isOpen}
                onCancel={onCancel}
                title='Disbursal Calculation'
                wrapClassName='loan-drawdown-disbursal-calculate-modal'
                width={848}
            >
                <Spin/>
            </Modal>
        );
    }

    const loanDrawdownItemsBlock: ReactElement[] = _.map(_.sortBy(drawdownItems, 'sortOrder'), (loanDrawdownItem: ILoanDrawdownItem) => {
        const onClickDelete: () => void = () => onClickDeleteItem(loanDrawdownItem.uuid);
        const onChangeAmount: (value: number) => void = (value: number) => onChangeItemAmount(loanDrawdownItem.uuid, value);
        const onChangeDescription: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => onChangeItemDescription(loanDrawdownItem.uuid, event);
        const onBlurDescription: (event: React.FocusEvent<HTMLInputElement>) => void = (event: React.FocusEvent<HTMLInputElement>) => validateLineItemDescription((loanDrawdownItem.uuid));
        const onBlurAmount: (event: React.FocusEvent<HTMLInputElement>) => void = (event: React.FocusEvent<HTMLInputElement>) => validateLineItemAmount((loanDrawdownItem.uuid));

        const isDescriptionInvalid: boolean = errors.items[loanDrawdownItem.uuid] && !!errors.items[loanDrawdownItem.uuid].description;
        const isAmountInvalid: boolean = errors.items[loanDrawdownItem.uuid] && !!errors.items[loanDrawdownItem.uuid].amount;

        return (
            <Space className='line-item' key={loanDrawdownItem.uuid}>
                <Form.Item help={isDescriptionInvalid && errors.items[loanDrawdownItem.uuid].description} validateStatus={isDescriptionInvalid && 'error'}>
                    <Input className='description' onBlur={onBlurDescription} onChange={onChangeDescription} value={loanDrawdownItem.description} />
                </Form.Item>
                <Form.Item help={isAmountInvalid && errors.items[loanDrawdownItem.uuid].amount} validateStatus={isAmountInvalid && 'error'}>
                    <InputNumber addonBefore='$' className='amount' onBlur={onBlurAmount} onChange={onChangeAmount} value={loanDrawdownItem.amount} />
                </Form.Item>
                <Button className='delete' onClick={onClickDelete} size='small' type='link'>Delete</Button>
            </Space>
        );
    });

    return (
        <Modal
            footer={footer}
            destroyOnClose={true}
            open={isOpen}
            onCancel={onCancel}
            title='Disbursal Calculation'
            wrapClassName='loan-drawdown-disbursal-calculate-modal'
            width={960}
        >
            <Space direction='vertical'>
                <FormItem className='amount-requested' colon={false} label='Amount Requested'>
                    {currencyFormatter.format(drawdown.requestedAmount)}
                </FormItem>
                <FormItem className='qs-certified-amount' colon={false} label='QS Certified Amount'>
                    {currencyFormatter.format(drawdown.qsCertifiedAmount)}
                </FormItem>
                <Form.Item
                    colon={false}
                    label='Approved Drawdown Amount'
                    help={errors.approvedDrawdownAmount}
                    validateStatus={errors.approvedDrawdownAmount && 'error'}
                    className='approved-drawdown-amount'
                >
                    <InputNumber addonBefore='$' onBlur={onBlurValidateApprovedDrawdownAmount} onChange={onChangeApprovedDrawdownAmount} type='number' value={drawdown.approvedDrawdownAmount} />
                </Form.Item>
                <Space direction='vertical'>
                    <Typography.Title className='fees-title' level={5}>Fees</Typography.Title>
                    {loanDrawdownItemsBlock}
                    <Button className='add-line-item' onClick={onClickAddLineItem}>Add Item</Button>
                </Space>
                <Form.Item className='total-disbursed' colon={false} label='Total to be Disbursed'>
                    {currencyFormatter.format(totalToBeDisbursed)}
                </Form.Item>
                <Form.Item
                    colon={false}
                    label='Transfer from Retained Funds'
                    help={errors.retainedFundsAmount}
                    validateStatus={errors.retainedFundsAmount && 'error'}
                    className='retained-fund-amount'
                >
                    <Space>
                        <InputNumber addonBefore='$' onBlur={onBlurValidateRetainedFundsAmount} onChange={onChangeRetainedFundsAmount} type='number' value={drawdown.retainedFundsAmount} />
                        <div className='remaining-balance'>({currencyFormatter.format(loan.retainedFunds - drawdown.retainedFundsAmount)} Remaining)</div>
                    </Space>
                </Form.Item>
                <Form.Item
                    colon={false}
                    label='Transfer from Retained Contingency'
                    help={errors.retainedContingencyAmount}
                    validateStatus={errors.retainedContingencyAmount && 'error'}
                    className='retained-contingency-amount'
                >
                    <Space>
                        <InputNumber addonBefore='$' onBlur={onBlurValidateRetainedContingencyAmount} onChange={onChangeRetainedContingencyAmount} type='number' value={drawdown.retainedContingencyAmount} />
                        <div className='remaining-balance'>({currencyFormatter.format(loan.retainedContingency - drawdown.retainedContingencyAmount) } Remaining)</div>
                    </Space>
                </Form.Item>
                <Form.Item className='disbursal-total' colon={false} help={errors.disbursalTotal} label='Disbursal Total' validateStatus={errors.disbursalTotal && 'error'}>
                    {currencyFormatter.format((drawdown.retainedContingencyAmount ?? 0) + (drawdown.retainedFundsAmount ?? 0))}
                </Form.Item>
            </Space>
        </Modal>
    );
}